import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  Chip,
  alpha,
  Avatar,
  Autocomplete,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Call as CallIcon,
  WhatsApp as WhatsAppIcon,
  Clear as ClearIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  PersonOutline as ReferrerIcon,
  AccessTime as DurationIcon,
  Login as LastLoginIcon,
  EventAvailable as RegisteredIcon,
  InfoOutlined as DetailsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

// Created By Cursor — digits for tel: / wa.me (same as Schools)
const normalizeDialPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  return digits;
};

// Created By Cursor
const formatOptionLabel = (option) =>
  String(option || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const FALLBACK_OPTIONS = [
  { search_option: "school_name", isChecked: true },
  { search_option: "school_code", isChecked: true },
  { search_option: "admin_name", isChecked: true },
  { search_option: "admin_phone", isChecked: true },
  { search_option: "school_address", isChecked: false },
  { search_option: "state", isChecked: false },
  { search_option: "country", isChecked: false },
];

// Created By Cursor — same status helpers as All Schools (Grace red + days left)
const getStatusColor = (status, school) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "grace" || s === "expired") return "error";
  if (s === "demo") return school?.demo_expired ? "warning" : "info";
  if (s === "demo not start") return "warning";
  if (s === "inactive") return "default";
  return "default";
};
const getStatusIcon = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return <ActiveIcon />;
  if (s === "grace" || s === "expired") return <CalendarIcon />;
  if (s === "demo" || s === "demo not start") return <CalendarIcon />;
  return <InactiveIcon />;
};
const getGraceDaysLeft = (school) => {
  if (school.grace_days_left != null && !Number.isNaN(Number(school.grace_days_left))) {
    return Math.max(0, Number(school.grace_days_left));
  }
  if (!school.grace_end_date) return null;
  const end = new Date(school.grace_end_date).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - Date.now()) / 86400000));
};
const getStatusLabel = (school) => {
  const s = String(school.status || "").toLowerCase();
  if (s === "grace") {
    const left = getGraceDaysLeft(school);
    if (left == null) return "Grace";
    if (left <= 0) return "Grace · Last day";
    return `Grace · ${left} day${left === 1 ? "" : "s"} left`;
  }
  if (s === "expired") return "Service Expired";
  if (s === "demo" && school.demo_label) {
    return school.demo_expired ? "Demo expired" : `Demo · ${school.demo_label}`;
  }
  return school.status || "Demo Not Start";
};

const SchoolSearch = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [indexKeywords, setIndexKeywords] = useState([]);
  const [searchOptions, setSearchOptions] = useState(FALLBACK_OPTIONS);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [indexLoading, setIndexLoading] = useState(true);
  const [searched, setSearched] = useState(false);
  const [lastKeyword, setLastKeyword] = useState("");

  // Created By Cursor — load autocomplete index + field options
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIndexLoading(true);
      const [indexRes, optionsRes] = await Promise.all([
        apiService.getSearchIndex(),
        apiService.getSearchOptions(),
      ]);
      if (cancelled) return;

      if (indexRes.success && Array.isArray(indexRes.data)) {
        const keywords = indexRes.data
          .map((row) => String(row.search_keyword || "").trim())
          .filter(Boolean);
        setIndexKeywords([...new Set(keywords)]);
      }

      if (optionsRes.success && Array.isArray(optionsRes.data) && optionsRes.data.length) {
        setSearchOptions(
          optionsRes.data.map((row) => ({
            search_option: row.search_option,
            isChecked:
              String(row.isChecked).toLowerCase() === "true" ||
              row.isChecked === true ||
              row.isChecked === 1 ||
              row.isChecked === "1",
          }))
        );
      }
      setIndexLoading(false);
      window.setTimeout(() => inputRef.current?.focus?.(), 80);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const checkedOptions = useMemo(
    () => searchOptions.filter((o) => o.isChecked).map((o) => o.search_option),
    [searchOptions]
  );

  // Created By Cursor
  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setSearched(false);
    setLastKeyword("");
    inputRef.current?.focus?.();
  };

  // Created By Cursor — same as Android isValidInput + GetterMySchoolsClass
  const runSearch = useCallback(async () => {
    const keyword = String(query || "").trim();
    if (!keyword) {
      toast.error("Enter Keyword");
      return;
    }
    if (!checkedOptions.length) {
      toast.error("Select Atleast one option");
      return;
    }

    setLoading(true);
    setSearched(true);
    setLastKeyword(keyword);
    const result = await apiService.searchSchools(keyword, checkedOptions);
    setLoading(false);

    if (result.success) {
      setResults(Array.isArray(result.data) ? result.data : []);
    } else {
      setResults([]);
      const msg = result.error || result.message || "No Record Found!!!";
      if (/no record|no schools/i.test(String(msg))) {
        toast.error("No Record Found!!!");
      } else {
        toast.error(msg);
      }
    }
  }, [query, checkedOptions]);

  // Created By Cursor — toggle; keep at least one selected
  const toggleOption = (index) => {
    setSearchOptions((prev) => {
      const next = prev.map((o, i) =>
        i === index ? { ...o, isChecked: !o.isChecked } : o
      );
      if (!next.some((o) => o.isChecked)) {
        toast.error("Select Atleast one option");
        return prev;
      }
      return next;
    });
  };

  const resultCount = results.length;
  const pageTitle = searched ? `Search Result (${resultCount})` : "Search School";
  const hint = useMemo(() => {
    if (loading) return "Searching…";
    if (!searched) return "Enter keyword and tap Search";
    if (resultCount === 0) return "No schools matched";
    return `${resultCount} school${resultCount === 1 ? "" : "s"} found`;
  }, [loading, searched, resultCount]);

  return (
    <PageContentLayout
      title={pageTitle}
      subtitle="Find schools by name, code, contact, or location"
      barSubtitle={hint}
      breadcrumbs={[
        { label: "Search School", icon: <SearchIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Button
          variant="outlined"
          size="small"
          startIcon={<SchoolIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/schools")}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 800,
            px: 1.5,
            borderWidth: 1.5,
            borderColor: alpha("#fff", 0.55),
            color: "#fff",
            bgcolor: alpha("#fff", 0.12),
            "&:hover": {
              borderWidth: 1.5,
              borderColor: "#fff",
              bgcolor: alpha("#fff", 0.22),
            },
          }}
        >
          All Schools
        </Button>
      }
    >
      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          width: "100%",
          background: `
            radial-gradient(ellipse 80% 50% at 10% -10%, ${alpha(crmColors.logo.light, 0.14)} 0%, transparent 55%),
            radial-gradient(ellipse 60% 40% at 100% 0%, ${alpha(crmColors.primary.main, 0.08)} 0%, transparent 50%)
          `,
          borderRadius: "20px",
          px: { xs: 0, sm: 0.5 },
          pb: 1,
        }}
      >
        {/* Unified search panel */}
        <Card
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: "20px",
            border: `1px solid ${crmColors.border.default}`,
            overflow: "hidden",
            bgcolor: "#fff",
            boxShadow: `0 10px 32px ${alpha(crmColors.primary.main, 0.07)}`,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.25 },
              pt: { xs: 1.75, sm: 2 },
              pb: 1.5,
              background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 42%, ${crmColors.hero.mid} 100%)`,
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                width: 180,
                height: 180,
                borderRadius: "50%",
                right: -40,
                top: -60,
                bgcolor: alpha("#fff", 0.08),
                pointerEvents: "none",
              },
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5, position: "relative" }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha("#fff", 0.18),
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <SearchIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#fff", lineHeight: 1.2 }}>
                  Find a school
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: alpha("#fff", 0.88), fontWeight: 500 }}>
                  Search across name, code, admin, phone &amp; location
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1.15,
                position: "relative",
              }}
            >
              <Autocomplete
                freeSolo
                fullWidth
                options={indexKeywords}
                inputValue={query}
                onInputChange={(_, value) => setQuery(value ?? "")}
                loading={indexLoading}
                filterOptions={(options, state) => {
                  const q = String(state.inputValue || "").toLowerCase().trim();
                  if (!q) return options.slice(0, 40);
                  return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 40);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={inputRef}
                    placeholder="Type school name, code, phone…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        runSearch();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: crmColors.primary.main, fontSize: 22 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {query ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="Clear keyword"
                                onClick={clearSearch}
                                sx={{
                                  color: crmColors.text.secondary,
                                  "&:hover": {
                                    color: crmColors.error.main,
                                    bgcolor: alpha(crmColors.error.main, 0.08),
                                  },
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 52,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        fontSize: 15,
                        fontWeight: 600,
                        boxShadow: `0 4px 16px ${alpha("#000", 0.12)}`,
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                )}
              />

              <Stack
                direction="row"
                spacing={1}
                sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
              >
                {query || searched ? (
                  <Button
                    variant="outlined"
                    onClick={clearSearch}
                    startIcon={<ClearIcon />}
                    sx={{
                      flex: { xs: 1, sm: "none" },
                      height: 52,
                      minWidth: { sm: 108 },
                      px: 2,
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: 14,
                      borderWidth: 1.5,
                      borderColor: alpha("#fff", 0.55),
                      color: "#fff",
                      bgcolor: alpha("#fff", 0.1),
                      "&:hover": {
                        borderWidth: 1.5,
                        borderColor: "#fff",
                        bgcolor: alpha("#fff", 0.2),
                      },
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  onClick={runSearch}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon sx={{ color: "#fff !important" }} />
                    )
                  }
                  sx={{
                    flex: { xs: 1.4, sm: "none" },
                    height: 52,
                    minWidth: { sm: 148 },
                    px: 3,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 900,
                    fontSize: 16,
                    letterSpacing: 0.3,
                    color: "#fff",
                    bgcolor: crmColors.energy.main,
                    border: `2px solid ${alpha("#fff", 0.95)}`,
                    boxShadow: `0 6px 20px ${alpha(crmColors.energy.dark, 0.45)}, 0 0 0 1px ${alpha("#000", 0.06)}`,
                    "& .MuiButton-startIcon": { color: "#fff" },
                    "&:hover": {
                      bgcolor: crmColors.energy.dark,
                      color: "#fff",
                      borderColor: "#fff",
                      boxShadow: `0 8px 24px ${alpha(crmColors.energy.dark, 0.55)}`,
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                      bgcolor: crmColors.energy.dark,
                    },
                    "&.Mui-disabled": {
                      bgcolor: alpha(crmColors.energy.main, 0.55),
                      color: "#fff",
                      borderColor: alpha("#fff", 0.5),
                    },
                    transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
                  }}
                >
                  {loading ? "Searching…" : "Search"}
                </Button>
              </Stack>
            </Box>
          </Box>

          {/* Search options — clear checkboxes */}
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.75,
              bgcolor: alpha(crmColors.primary.main, 0.02),
              borderTop: `1px solid ${crmColors.border.default}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              sx={{ mb: 1.35 }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: crmColors.text.primary }}>
                Search in
              </Typography>
              <Chip
                size="small"
                label={`${checkedOptions.length} selected`}
                sx={{
                  height: 24,
                  fontWeight: 800,
                  fontSize: 11,
                  bgcolor: alpha(crmColors.primary.main, 0.1),
                  color: crmColors.primary.main,
                }}
              />
            </Stack>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 0.85,
                overflowX: "auto",
                pb: 0.5,
                mx: { xs: -0.25, sm: 0 },
                px: { xs: 0.25, sm: 0 },
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha(crmColors.primary.main, 0.25),
                  borderRadius: 8,
                },
              }}
            >
              {searchOptions.map((opt, index) => {
                const checked = !!opt.isChecked;
                return (
                  <FormControlLabel
                    key={opt.search_option}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleOption(index)}
                        icon={
                          <CheckBoxOutlineBlankIcon
                            sx={{ fontSize: 20, color: crmColors.border.hover }}
                          />
                        }
                        checkedIcon={
                          <CheckBoxIcon sx={{ fontSize: 20, color: crmColors.primary.main }} />
                        }
                        sx={{
                          p: 0.5,
                          color: crmColors.border.hover,
                          "&.Mui-checked": { color: crmColors.primary.main },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontWeight: checked ? 800 : 700,
                          fontSize: 12.5,
                          color: checked ? crmColors.primary.main : crmColors.text.primary,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          pr: 0.35,
                        }}
                      >
                        {formatOptionLabel(opt.search_option)}
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      flexShrink: 0,
                      alignItems: "center",
                      minHeight: 40,
                      px: 0.85,
                      py: 0.25,
                      borderRadius: "10px",
                      border: `1.5px solid ${
                        checked ? crmColors.primary.main : crmColors.border.default
                      }`,
                      bgcolor: checked ? alpha(crmColors.primary.main, 0.08) : "#fff",
                      boxShadow: checked
                        ? `0 2px 8px ${alpha(crmColors.primary.main, 0.12)}`
                        : "none",
                      transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: crmColors.primary.main,
                        bgcolor: checked
                          ? alpha(crmColors.primary.main, 0.12)
                          : alpha(crmColors.primary.main, 0.04),
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Card>

        {/* Results area */}
        {loading ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              borderRadius: "18px",
              border: `1px solid ${crmColors.border.default}`,
              bgcolor: alpha("#fff", 0.85),
            }}
          >
            <CircularProgress size={32} sx={{ color: crmColors.primary.main, mb: 1.5 }} />
            <Typography sx={{ fontWeight: 800, color: crmColors.text.primary }}>
              Searching schools…
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Looking for “{lastKeyword || query}”
            </Typography>
          </Box>
        ) : !searched ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "18px",
              border: `1px dashed ${alpha(crmColors.primary.main, 0.28)}`,
              background: `linear-gradient(160deg, ${alpha(crmColors.primary.main, 0.05)} 0%, ${alpha("#fff", 0.9)} 60%)`,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 1.5,
                borderRadius: "18px",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.logo.light})`,
                color: "#fff",
                boxShadow: `0 10px 24px ${alpha(crmColors.primary.main, 0.28)}`,
              }}
            >
              <SchoolIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: crmColors.text.primary, mb: 0.35 }}>
              Ready to search
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: "auto" }}>
              Tick the fields above, enter a keyword, then tap Search to find matching schools.
            </Typography>
          </Box>
        ) : resultCount === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "18px",
              border: `1px dashed ${alpha(crmColors.warning.main, 0.35)}`,
              background: `linear-gradient(160deg, ${alpha(crmColors.warning.main, 0.07)} 0%, ${alpha("#fff", 0.92)} 60%)`,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: "auto",
                mb: 1.25,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(crmColors.warning.main, 0.14),
                color: crmColors.warning.dark,
              }}
            >
              <SearchIcon />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: crmColors.text.primary, mb: 0.4 }}>
              No Record Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No schools matched “{lastKeyword}”
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                onClick={clearSearch}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "10px",
                  borderColor: crmColors.border.primaryTint,
                }}
              >
                Try again
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate("/schools")}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "10px",
                  bgcolor: crmColors.primary.main,
                  boxShadow: "none",
                }}
              >
                Browse All Schools
              </Button>
            </Stack>
          </Box>
        ) : (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5, px: 0.25 }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: crmColors.text.primary }}>
                Results for “{lastKeyword}”
              </Typography>
              <Chip
                size="small"
                label={`${resultCount} found`}
                sx={{
                  height: 24,
                  fontWeight: 800,
                  fontSize: 11,
                  bgcolor: alpha(crmColors.success.main, 0.12),
                  color: crmColors.success.dark,
                }}
              />
            </Stack>

            {/* Same card layout as All Schools */}
            <Grid container spacing={1.75}>
              {results.map((school, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={school.school_code || `${school.school_id}-${index}`}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2.5,
                      border: `1px solid ${crmColors.border.default}`,
                      boxShadow: "none",
                      background: "#fff",
                      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": {
                        borderColor: crmColors.border.primaryTint,
                        boxShadow: `0 8px 20px ${alpha(crmColors.primary.main, 0.1)}`,
                      },
                      "&::before": { display: "none" },
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, overflow: "visible" }}>
                      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.5 }}>
                        <Avatar
                          src={school.school_logo_path || undefined}
                          sx={{
                            width: 44,
                            height: 44,
                            fontSize: 16,
                            fontWeight: 800,
                            bgcolor: crmColors.primary.main,
                            flexShrink: 0,
                          }}
                        >
                          {(school.school_name || "S").charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: 14,
                              lineHeight: 1.3,
                              mb: 0.35,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {school.school_name}
                            {school.school_code ? (
                              <Typography
                                component="span"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: crmColors.text.secondary,
                                  ml: 0.5,
                                }}
                              >
                                ({school.school_code})
                              </Typography>
                            ) : null}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: crmColors.text.secondary,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.35,
                            }}
                          >
                            {school.school_address || "—"}
                          </Typography>
                        </Box>
                        <Chip
                          icon={getStatusIcon(school.status)}
                          label={getStatusLabel(school)}
                          color={getStatusColor(school.status, school)}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            borderRadius: 1.5,
                            height: 24,
                            maxWidth: 220,
                            flexShrink: 0,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              px: 0.75,
                            },
                          }}
                        />
                      </Box>

                      <Stack spacing={0.75} sx={{ mb: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontWeight: 700,
                              color: crmColors.text.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                color: crmColors.text.secondary,
                              }}
                            >
                              Admin:
                            </Typography>{" "}
                            {school.admin_name || "—"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: crmColors.text.secondary,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {school.admin_phone || "—"}
                            </Typography>
                            {normalizeDialPhone(school.admin_phone) ? (
                              <>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={`tel:+${normalizeDialPhone(school.admin_phone)}`}
                                  title="Call"
                                  aria-label="Call admin"
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: alpha(crmColors.primary.main, 0.1),
                                    color: crmColors.primary.main,
                                    "&:hover": {
                                      bgcolor: alpha(crmColors.primary.main, 0.18),
                                    },
                                  }}
                                >
                                  <CallIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={`https://wa.me/${normalizeDialPhone(school.admin_phone)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="WhatsApp"
                                  aria-label="WhatsApp admin"
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    bgcolor: alpha("#25D366", 0.12),
                                    color: "#128C7E",
                                    "&:hover": { bgcolor: alpha("#25D366", 0.22) },
                                  }}
                                >
                                  <WhatsAppIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </>
                            ) : null}
                          </Box>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: crmColors.text.secondary,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 14, color: crmColors.primary.main }} />
                          {[school.state, school.country].filter(Boolean).join(", ") || "—"}
                        </Typography>
                        {(school.referred_by || school.referal_code) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: crmColors.primary.main,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.5,
                              fontWeight: 700,
                              lineHeight: 1.35,
                              mt: 0.25,
                            }}
                          >
                            <ReferrerIcon sx={{ fontSize: 14, mt: "1px", flexShrink: 0 }} />
                            <Box component="span" sx={{ minWidth: 0, wordBreak: "break-word" }}>
                              {school.referred_by
                                ? `Referred by ${school.referred_by}${
                                    school.referred_by_school_code
                                      ? ` (${school.referred_by_school_code})`
                                      : ""
                                  }`
                                : `Referral: ${school.referal_code}`}
                            </Box>
                          </Typography>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Chip
                            size="small"
                            icon={<PeopleIcon sx={{ fontSize: "14px !important" }} />}
                            label={`${Number(school.total_student || 0).toLocaleString()} students`}
                            sx={{
                              height: 26,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: "8px",
                              bgcolor: alpha(crmColors.primary.main, 0.1),
                              color: crmColors.primary.main,
                              "& .MuiChip-icon": { color: crmColors.primary.main },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<RegisteredIcon sx={{ fontSize: "14px !important" }} />}
                            label={
                              school.date_added
                                ? new Date(school.date_added).toLocaleDateString()
                                : "No date"
                            }
                            sx={{
                              height: 26,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: "8px",
                              bgcolor: alpha(crmColors.hero.start, 0.1),
                              color: crmColors.hero.start,
                              "& .MuiChip-icon": { color: crmColors.hero.start },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<DurationIcon sx={{ fontSize: "14px !important" }} />}
                            label={school.duration || "—"}
                            sx={{
                              height: 26,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: "8px",
                              bgcolor: alpha(crmColors.energy.main, 0.12),
                              color: crmColors.energy.dark,
                              "& .MuiChip-icon": { color: crmColors.energy.main },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<LastLoginIcon sx={{ fontSize: "14px !important" }} />}
                            label={
                              school.last_login_date || school.last_login
                                ? school.last_login_date ||
                                  (school.last_login
                                    ? new Date(school.last_login).toLocaleString()
                                    : "Never")
                                : "Never"
                            }
                            sx={{
                              height: 26,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: "8px",
                              bgcolor: alpha(crmColors.text.secondary, 0.1),
                              color: crmColors.text.secondary,
                              "& .MuiChip-icon": { color: crmColors.text.secondary },
                            }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<DetailsIcon sx={{ fontSize: 15 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (school.school_code) {
                                navigate(
                                  `/schools/detail/${encodeURIComponent(school.school_code)}`
                                );
                              }
                            }}
                            disabled={!school.school_code}
                            sx={{
                              textTransform: "none",
                              height: 26,
                              minHeight: 26,
                              px: 1.25,
                              borderRadius: "8px",
                              fontWeight: 800,
                              fontSize: 11,
                              background: crmColors.primary.main,
                              backgroundImage: "none",
                              color: "#fff",
                              "&:hover": {
                                background: crmColors.primary.dark,
                                backgroundImage: "none",
                              },
                            }}
                          >
                            Details
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </PageContentLayout>
  );
};

export default SchoolSearch;
